import { useMemo, useRef } from 'react';
import { v1 } from 'uuid';
import { find, pull, slice, sortBy, toLower } from 'lodash';
import { useDataSource } from '../../../services/ApiDataService';

const DEFAULT_PROBLEM_ROWLIMIT = 250;
const MAX_PROBLEMS_PER_GROUPING = 5;
const MAX_PROBLEMS = 10;

const PROBLEMS_DATASOURCE = {
    url: '/query/problems',
};

const STATUS_DATASOURCE = {
    url: '/query/chart',
    params: {
        type: 'site',
        metrics: 'status',
    },
};

const BS_STATUS_DATASOURCE = {
    url: '/query/chart',
    params: {
        type: 'businessservice',
        metrics: ['bsSiteCounts'],
        invertEntityFilters: true,
    },
};

export function useSiteProblems(options) {
    // useRef so we get a new uuid for each new site, but only
    // for different components. Generate it initially and keep it.
    const requestId = useRef(v1());

    const { rowLimit = DEFAULT_PROBLEM_ROWLIMIT, rowStart = 0, type = 'all' } = options;

    const site = options.site?.uuid;
    const bsGroup = options?.bsGroup;
    const start = options.timeRange?.start || 0;
    const end = options.timeRange?.end || 0;

    const dataParams = useMemo(
        () => ({
            start,
            end,
            requestId: requestId.current,
            site,
            rowLimit,
            rowStart,
            type,
            ...bsGroup,
        }),
        [rowLimit, rowStart, site, end, start, type, bsGroup]
    );

    const [response, isLoading, error] = useDataSource(PROBLEMS_DATASOURCE, dataParams);

    if (response?.data) {
        response.data = assignProblemIds(response.data);
    }

    return [response, isLoading, error];
}

export function useGroupedSiteProblems(options) {
    const [data, isLoading, error] = useSiteProblems(options);

    const groupedData = useMemo(() => {
        if (isLoading || !data || error) {
            return {
                hasProblems: false,
                total: 0,
                groupedProblems: [],
            };
        }

        const groupedProblems = groupProblems(data);

        return {
            data,
            groupedProblems,
            hasProblems: data?.data?.length > 0,
            total: data.total,
        };
    }, [data, error, isLoading]);

    return [groupedData, isLoading, error];
}

export function useSiteStatusTrend(options) {
    const { site, timeRange, bsGroup } = options;

    const dataParams = useMemo(
        () => ({ site: site?.uuid, ...(timeRange || {}), ...(bsGroup || {}) }),
        [site, timeRange, bsGroup]
    );
    const [data, isLoading, error] = useDataSource(bsGroup ? BS_STATUS_DATASOURCE : STATUS_DATASOURCE, dataParams);

    return [data?.data || [], isLoading, error];
}

/**
 * Manually assign IDs to our problems, because we don't have them on the backend
 * for whatever reason. We need IDs for unique checking and React key indexing.
 * @param {Object[]} problems
 */
function assignProblemIds(problems) {
    return problems.map((problem) => ({ ...problem, problemId: v1() }));
}

function groupProblems(problems) {
    const { bsMap: nameMapping, data } = problems;

    let groupedProblems = data.reduce(reduceProblemsToParents, []);

    const hasGroupings = hasBusinessServiceGroupings(groupedProblems);
    const groupLimit = hasGroupings ? MAX_PROBLEMS_PER_GROUPING : MAX_PROBLEMS;
    groupedProblems = groupedProblems.map((item) => limitMaxProblems(item, groupLimit));

    return sortGroupedProblems(groupedProblems);

    function reduceProblemsToParents(items, problem) {
        const {
            entity: { parents },
        } = problem;

        // this goes in the "other" category, which has an id of "null" in UI-land.
        if (!parents || !parents?.length) {
            assignProblemToParent(items, problem, null);
        } else {
            parents.forEach((parentId) => assignProblemToParent(items, problem, parentId));
        }

        return items;
    }

    function assignProblemToParent(items, problem, parentId) {
        let parent = find(items, { id: parentId });

        // add the parent to items if it does not exist
        if (!parent) {
            parent = {
                totalProblems: 0,
                id: parentId || null,
                name: nameMapping[parentId] || null,
                problems: [],
            };

            items.push(parent);
        }

        parent.problems.push(problem);
    }
}

function limitMaxProblems(item, limit) {
    item.totalProblems = item.problems.length;

    if (item.problems.length > limit) {
        item.problems = slice(item.problems, 0, limit);
    }

    return item;
}

/**
 * Sorts our grouped problems by name, and then makes sure the "other" group comes last
 * @param {Object[]} groupedProblems
 */
function sortGroupedProblems(groupedProblems) {
    const sorted = sortBy(groupedProblems, ({ name }) => toLower(name));
    const otherGroup = find(sorted, { id: null });

    if (otherGroup) {
        pull(sorted, otherGroup);
        return [...sorted, otherGroup];
    }

    return sorted;
}

/**
 * Returns whether or not the grouped problems contain business services,
 * or if there is nothing/only the "other" group.
 * @param {Object[]} groupedProblems
 */
export function hasBusinessServiceGroupings(groupedProblems) {
    if (!groupedProblems || groupedProblems?.length === 0) {
        return false;
    }

    if (groupedProblems.length > 1) {
        return true;
    }

    const [{ id: firstGroupId }] = groupedProblems;

    // if only one grouping, and it's ID is not null, that means all
    // of the problems belong to a single business service.
    return firstGroupId !== null;
}

/**
 * Counts the number of unique problems across a page-limited set of grouped
 * problems. The same problem can appear under multiple groupings of business services.
 *
 * @param {Object[]} groupedProblems
 */
export function useGroupedProblemCount(groupedProblems) {
    return useMemo(() => getGroupedProblemCount(groupedProblems), [groupedProblems]);
}

function getGroupedProblemCount(groupedProblems) {
    if (!groupedProblems || groupedProblems.length === 0) {
        return 0;
    }

    const problemIds = {};

    return groupedProblems.reduce((total, group) => {
        let count = 0;

        group.problems.forEach((problem) => {
            if (!problemIds[problem.problemId]) {
                count += 1;
                problemIds[problem.problemId] = true;
            }
        });

        return total + count;
    }, 0);
}
